import { useState } from 'react';
import { BarChart3, Download, ExternalLink, Info, PauseCircle, PlayCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/Button';
import { CampaignSteps } from '@/components/CampaignSteps';
import { ClickMap } from '@/components/ClickMap';
import { StopSendDialog } from '@/components/StopSendDialog';
import { CampaignStatusPill } from '@/components/StatusPill';
import { EmptyState, ErrorState, Skeleton } from '@/components/States';
import { useCampaign } from '@/hooks/use-campaigns';
import { useResume } from '@/hooks/use-campaign-flow';
import { useReport } from '@/hooks/use-send';
import { useRouteParam } from '@/hooks/use-route-param';
import { useOpenCounting, type OpenCounting } from '@/hooks/use-open-counting';
import { button } from '@/components/button-variants';
import { Roles, type Session } from '@/auth/keycloak';
import { formatDateTime } from '@/lib/utils';
import type { CampaignDetail, CampaignReport, RateSet } from '@/lib/schemas';

/**
 * The campaign report.
 *
 * Human and machine engagement are shown SEPARATELY and the human figure is the headline, because a
 * blended open rate for an Apple-heavy audience is roughly "how many recipients use Apple Mail". The
 * machine number is still shown — it is a real deliverability signal — but never as the number
 * somebody would quote.
 */
export function ReportPage({ session }: { session: Session }) {
  const campaignId = useRouteParam('campaignId');
  const { counting, setCounting, isHuman } = useOpenCounting();

  const { data, isPending, isError, error, refetch } = useReport(campaignId);

  // For the step nav only. The report itself has everything else it needs.
  const campaign = useCampaign(campaignId);

  if (isPending) return <ReportSkeleton />;

  if (isError) {
    return (
      <div className="px-4 py-6 sm:px-6">
        <ErrorState error={error} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-6">
      {campaign.data !== undefined && (
        <div className="mb-4">
          <CampaignSteps campaign={campaign.data} />
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-heading">{data.name}</h1>

          <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted">
            <CampaignStatusPill status={data.status} />
            {data.sentAt !== null && <span>Finished {formatDateTime(data.sentAt)}</span>}
            {data.sentAt === null && data.sendingStartedAt !== null && (
              <span>Started {formatDateTime(data.sendingStartedAt)}</span>
            )}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {campaign.data !== undefined && <InFlightControls campaign={campaign.data} />}
          <Exports campaignId={campaignId} session={session} />
          <OpenCountingToggle counting={counting} onChange={setCounting} />
        </div>
      </div>

      {data.overall.sent === 0 ? (
        <div className="mt-6 rounded-lg border border-border bg-surface">
          <EmptyState
            icon={BarChart3}
            title="Nothing to report yet"
            description="This campaign has not sent to anyone. Numbers appear here as the event stream comes back, usually within a minute of the first delivery."
          />
        </div>
      ) : (
        <>
          <Headline rates={data.overall} isHuman={isHuman} />

          <OpenCountingCaveat isHuman={isHuman} />

          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <Deliverability rates={data.overall} />
            <TopLinks links={data.topLinks} />
          </div>

          {/* The map below, the ranking above. They answer different questions — "what was popular"
              against "did position matter" — and the ranking is capped at five so it stays a glance
              rather than a second copy of the table. */}
          <div className="mt-6">
            <ClickMap campaignId={campaignId} />
          </div>

          {data.arms.length > 0 && <ArmComparison arms={data.arms} isHuman={isHuman} />}
        </>
      )}
    </div>
  );
}

/**
 * The four numbers people actually look at.
 *
 * Each carries its DENOMINATOR in the caption, because "43% open rate" means nothing without knowing
 * what it is 43% of — and the whole substance of this report is that engagement is over delivered
 * rather than sent.
 */
function Headline({ rates, isHuman }: { rates: RateSet; isHuman: boolean }) {
  return (
    <div className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <Metric
        label="Delivered"
        value={rates.delivered.toLocaleString()}
        caption={`${rates.deliveryRate.toFixed(1)}% of ${rates.sent.toLocaleString()} sent`}
      />

      <Metric
        label={isHuman ? 'Opened' : 'Opened, incl. automated'}
        value={`${(isHuman ? rates.humanOpenRate : rates.openRate).toFixed(1)}%`}
        caption={
          isHuman
            ? `${rates.uniqueHumanOpens.toLocaleString()} people, of ${rates.delivered.toLocaleString()} delivered`
            : `${rates.uniqueOpens.toLocaleString()} opens, of ${rates.delivered.toLocaleString()} delivered`
        }
        // The OTHER figure sits underneath rather than being folded in, whichever way round the
        // toggle is — so the number on screen is never the only one available.
        footnote={
          rates.uniqueOpens > rates.uniqueHumanOpens
            ? isHuman
              ? `${(rates.uniqueOpens - rates.uniqueHumanOpens).toLocaleString()} more were automated (Apple Mail Privacy and similar), excluded here`
              : `${rates.uniqueHumanOpens.toLocaleString()} of these look like people`
            : undefined
        }
      />

      <Metric
        label={isHuman ? 'Clicked' : 'Clicked, incl. scanners'}
        value={`${(isHuman ? rates.humanClickRate : rates.clickRate).toFixed(1)}%`}
        caption={
          isHuman
            ? `${rates.uniqueHumanClicks.toLocaleString()} people, of ${rates.delivered.toLocaleString()} delivered`
            : `${rates.uniqueClicks.toLocaleString()} clicks, of ${rates.delivered.toLocaleString()} delivered`
        }
        // Click-to-open is recomputed rather than read: the API returns it over HUMAN opens, and
        // mixing a blended numerator with a filtered denominator produces a rate that is neither.
        footnote={`${clickToOpen(rates, isHuman).toFixed(1)}% of the ${isHuman ? 'people who opened it' : 'recorded opens'}`}
      />

      <Metric
        label="Complaints"
        value={`${rates.complaintRate.toFixed(2)}%`}
        caption={`${rates.complained.toLocaleString()} of ${rates.delivered.toLocaleString()} delivered`}
        // 0.1% is our own breaker threshold and 0.30% is Gmail's ceiling. Worth flagging at a glance,
        // and not by colour alone.
        tone={rates.complaintRate > 0.1 ? 'danger' : 'neutral'}
        footnote={rates.complaintRate > 0.1 ? 'Above the 0.1% threshold' : undefined}
      />
    </div>
  );
}

function Metric({
  label,
  value,
  caption,
  footnote,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  caption: string;
  footnote?: string | undefined;
  tone?: 'neutral' | 'danger';
}) {
  return (
    <div className="rounded-lg border border-border bg-surface p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-muted">{label}</p>

      <p
        className={
          tone === 'danger'
            ? 'mt-1 text-2xl font-semibold tabular-nums text-danger-ink'
            : 'mt-1 text-2xl font-semibold tabular-nums text-heading'
        }
      >
        {value}
      </p>

      <p className="mt-1 text-xs text-muted">{caption}</p>

      {footnote !== undefined && (
        <p className="mt-2 border-t border-border pt-2 text-xs text-muted">{footnote}</p>
      )}
    </div>
  );
}

/**
 * Bounces, blocks and drops, kept apart.
 *
 * A single "failed" figure would be easier to render and actively unhelpful: the fix for a reputation
 * block is nothing like the fix for a list full of dead addresses.
 */
function Deliverability({ rates }: { rates: RateSet }) {
  const rows = [
    { label: 'Hard bounced', value: rates.hardBounced, hint: 'The address does not exist' },
    { label: 'Blocked', value: rates.blocked, hint: 'Refused on reputation or content, not a dead address' },
    { label: 'Suppressed before sending', value: rates.dropped, hint: 'Opted out between the render and the send' },
    { label: 'Unsubscribed', value: rates.unsubscribed, hint: `${rates.unsubscribeRate.toFixed(2)}% of delivered` },
  ];

  return (
    <section className="rounded-lg border border-border bg-surface">
      <h2 className="border-b border-border px-4 py-3 text-sm font-semibold text-heading">
        Deliverability
      </h2>

      <dl className="divide-y divide-border">
        {rows.map((row) => (
          <div key={row.label} className="flex items-baseline justify-between gap-4 px-4 py-2.5">
            <div>
              <dt className="text-sm text-foreground">{row.label}</dt>
              <dd className="text-xs text-muted">{row.hint}</dd>
            </div>

            <dd className="shrink-0 text-sm font-medium tabular-nums text-heading">
              {row.value.toLocaleString()}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/** Ordered by HUMAN clicks server-side — ranking on raw clicks puts scanner traffic on top. */
/**
 * The five most-clicked links, as a summary.
 *
 * Capped at five deliberately. It used to list up to 50, which made it a click-ordered duplicate of
 * the click map below — and a dashboard with the same data twice teaches people to skim past both.
 */
function TopLinks({ links }: { links: CampaignReport['topLinks'] }) {
  const top = links.slice(0, 5);

  return (
    <section className="rounded-lg border border-border bg-surface">
      <h2 className="border-b border-border px-4 py-3 text-sm font-semibold text-heading">
        Most clicked
      </h2>

      {top.length === 0 ? (
        <p className="px-4 py-6 text-sm text-muted">
          No clicks recorded yet. Links appear here once somebody clicks one.
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {top.map((link) => (
            <li key={`${link.position}-${link.url}`} className="flex items-baseline gap-3 px-4 py-2.5">
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5 truncate text-sm text-foreground">
                  <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted" aria-hidden="true" />
                  {link.linkText ?? link.url}
                </span>

                {link.linkText !== null && (
                  <span className="block truncate text-xs text-muted">{link.url}</span>
                )}
              </span>

              <span className="shrink-0 text-sm font-medium tabular-nums text-heading">
                {link.humanTotalClicks.toLocaleString()}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Only rendered for a real A/B test — a one-arm "comparison" is noise. */
function ArmComparison({ arms, isHuman }: { arms: CampaignReport['arms']; isHuman: boolean }) {
  return (
    <section className="mt-6 overflow-hidden rounded-lg border border-border bg-surface">
      <h2 className="border-b border-border px-4 py-3 text-sm font-semibold text-heading">
        Arms
      </h2>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-border bg-surface-2 text-xs uppercase tracking-wide text-muted">
            <tr>
              <th scope="col" className="px-4 py-2 font-medium">Arm</th>
              <th scope="col" className="px-4 py-2 font-medium">Delivered</th>
              <th scope="col" className="px-4 py-2 font-medium">
                {isHuman ? 'Opened' : 'Opened, all'}
              </th>
              <th scope="col" className="px-4 py-2 font-medium">
                {isHuman ? 'Clicked' : 'Clicked, all'}
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-border">
            {arms.map((arm) => (
              <tr key={arm.armId}>
                <td className="px-4 py-2">
                  <span className="flex items-center gap-2 font-medium text-heading">
                    {arm.label}
                    {/* A word, not just a colour — the winner has to be identifiable without hue. */}
                    {arm.isWinner && (
                      <span className="rounded bg-success-bg px-1.5 py-0.5 text-xs text-success-ink">
                        Winner
                      </span>
                    )}
                  </span>
                  <span className="block truncate text-xs text-muted">{arm.subject}</span>
                </td>
                <td className="px-4 py-2 tabular-nums text-muted">
                  {arm.rates.delivered.toLocaleString()}
                </td>
                <td className="px-4 py-2 tabular-nums text-muted">
                  {(isHuman ? arm.rates.humanOpenRate : arm.rates.openRate).toFixed(1)}%
                </td>
                <td className="px-4 py-2 tabular-nums text-muted">
                  {(isHuman ? arm.rates.humanClickRate : arm.rates.clickRate).toFixed(1)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * The CSV downloads.
 *
 * Plain anchors, not fetch-and-blob: the browser streams the response straight to disk, which is the
 * whole point of the endpoint streaming rather than buffering. A blob would pull tens of megabytes into
 * the tab's memory first, which is the failure the server-side design exists to avoid.
 *
 * The recipient export appears only for the roles that can run it. That is a UX affordance rather than
 * a control — the endpoint enforces the same rule server-side — but showing a button that always 403s
 * is worse than not showing it.
 */
function Exports({ campaignId, session }: { campaignId: string; session: Session }) {
  const canExportRecipients =
    session.roles.includes(Roles.Admin) || session.roles.includes(Roles.Sender);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <a
        href={`/api/v1/campaigns/${campaignId}/export/links.csv`}
        download
        className={button({ variant: 'ghost', size: 'sm' })}
      >
        <Download className="h-3.5 w-3.5" aria-hidden="true" />
        Links CSV
      </a>

      {canExportRecipients && (
        <a
          href={`/api/v1/campaigns/${campaignId}/export/recipients.csv`}
          download
          className={button({ variant: 'ghost', size: 'sm' })}
        >
          <Download className="h-3.5 w-3.5" aria-hidden="true" />
          Recipients CSV
        </a>
      )}
    </div>
  );
}

/**
 * The machine-open toggle (spec § 7).
 *
 * `aria-pressed` on two buttons rather than a checkbox, because this is not on/off — it is a choice
 * between two ways of counting, and a checkbox labelled "filter machine opens" makes the unchecked
 * state look like "unfiltered truth" when neither figure is that.
 */
function OpenCountingToggle({
  counting,
  onChange,
}: {
  counting: OpenCounting;
  onChange: (next: OpenCounting) => void;
}) {
  return (
    <div
      role="group"
      aria-label="How opens and clicks are counted"
      className="flex gap-1 rounded-md border border-border bg-surface p-1"
    >
      {(
        [
          { value: 'human', label: 'People' },
          { value: 'all', label: 'Including automated' },
        ] as const
      ).map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          aria-pressed={counting === option.value}
          className={
            counting === option.value
              ? 'rounded px-2.5 py-1 text-xs font-medium text-heading bg-chip'
              : 'rounded px-2.5 py-1 text-xs text-muted hover:bg-chip'
          }
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/**
 * The caveat the spec asks for, and it is not boilerplate.
 *
 * The filter is imperfect in BOTH directions: a genuine open by an Apple Mail Privacy recipient is also
 * flagged as automated, so for those contacts the human figure undercounts real readers while the
 * blended one overcounts. Saying only "we filtered out the robots" would be a claim to precision this
 * data does not have.
 */
function OpenCountingCaveat({ isHuman }: { isHuman: boolean }) {
  return (
    <p className="mt-3 flex gap-2 text-xs text-muted">
      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />

      <span>
        {isHuman
          ? 'Opens and clicks count people: prefetches by Apple Mail Privacy and similar services are excluded. Not exact in either direction — a real open by an Apple Mail Privacy recipient is flagged the same way, so this figure understates readers on those clients.'
          : 'Opens and clicks count every recorded event, including image prefetches by Apple Mail Privacy and clicks by security scanners. Use this to compare against another tool, which will almost certainly report this figure — not to decide who to follow up with.'}
      </span>
    </p>
  );
}

/**
 * Click-to-open, computed rather than read from the API.
 *
 * The API returns it over HUMAN opens, which is right for the default view and wrong for the other
 * one: a blended numerator over a filtered denominator is a rate that describes neither population,
 * and on an Apple-heavy list it can exceed 100%.
 */
function clickToOpen(rates: RateSet, isHuman: boolean): number {
  if (isHuman) return rates.clickToOpenRate;

  return rates.uniqueOpens === 0 ? 0 : (rates.uniqueClicks / rates.uniqueOpens) * 100;
}

/** Matched to the real layout's dimensions, so content arriving does not move the page. */
function ReportSkeleton() {
  return (
    <div className="px-4 py-6 sm:px-6">
      <Skeleton className="h-7 w-64" />
      <Skeleton className="mt-2 h-4 w-40" />

      <div className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((index) => (
          <Skeleton key={index} className="h-28" />
        ))}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Skeleton className="h-56" />
        <Skeleton className="h-56" />
      </div>
    </div>
  );
}

/**
 * Pause, resume and cancel — the controls for a send that is happening right now.
 *
 * On the REPORT rather than the review screen, because this is where somebody is when they realise
 * something is wrong: watching the numbers climb. The review screen is where you go before a send;
 * this is where you go during one, and putting the stop button anywhere else means a tab change at
 * the worst possible moment.
 *
 * Only rendered while there is something to stop. A pause button on a finished campaign is a control
 * that can only disappoint.
 */
function InFlightControls({ campaign }: { campaign: CampaignDetail }) {
  const [stopping, setStopping] = useState<'pause' | 'cancel' | null>(null);
  const resume = useResume(campaign.id);

  if (campaign.status !== 'Sending' && campaign.status !== 'Scheduled') return null;

  return (
    <>
      {campaign.isPaused ? (
        <>
          <Button
            variant="primary"
            size="sm"
            disabled={resume.isPending}
            onClick={() => resume.mutate()}
          >
            <PlayCircle className="h-3.5 w-3.5" aria-hidden="true" />
            {resume.isPending ? 'Resuming…' : 'Resume'}
          </Button>

          {/* The reason it was paused, because "paused" without it is a mystery to whoever sees it
              next — and that is often not the person who paused it. */}
          {campaign.pauseReason !== null && (
            <span className="text-xs text-muted">Paused: {campaign.pauseReason}</span>
          )}
        </>
      ) : (
        <Button variant="secondary" size="sm" onClick={() => setStopping('pause')}>
          <PauseCircle className="h-3.5 w-3.5" aria-hidden="true" />
          Pause
        </Button>
      )}

      <Button variant="ghost" size="sm" onClick={() => setStopping('cancel')}>
        <XCircle className="h-3.5 w-3.5" aria-hidden="true" />
        Cancel
      </Button>

      {stopping !== null && (
        <StopSendDialog
          campaignId={campaign.id}
          campaignName={campaign.name}
          mode={stopping}
          onClose={() => setStopping(null)}
        />
      )}
    </>
  );
}
